import qs from 'qs';
import { acquireToken } from '@alixpartners/ui-utils';
import { handleApiError } from 'components/ApiErrorNotification';
import { navigateToAccessDenied } from '@alixpartners/ui-components';
import {
	isDemoMode,
	updateUrlToDemoMode,
	fetchStoredQuery,
	createStoredQuery,
} from './apiRequests';

export const BASE_API_HEADERS = {
	Accept: 'application/json',
	'Content-Type': 'application/json',
};

export const DEFAULT_RETRIES = 3;

export function getApiUrl(resource, isMock = false) {
	const { REACT_APP_API_URL, REACT_APP_MOCK_API_URL } = process.env;
	const apiUrl = isMock ? REACT_APP_MOCK_API_URL : REACT_APP_API_URL;

	if (typeof resource === 'function') {
		return props => {
			const path = resource(props);
			return path ? `//${apiUrl}${path}` : null;
		};
	}

	return resource ? `//${apiUrl}${resource}` : null;
}

// TODO: Investigate why Promisifying this function breaks the auth when session expires
export function getRequestHeaders(token, auth = true) {
	if (auth) {
		return { ...BASE_API_HEADERS, Authorization: `Bearer ${token}` };
	} else {
		return BASE_API_HEADERS;
	}
}

export function getResourceWithParams(resource, params) {
	return `${resource}?${qs.stringify(params, { arrayFormat: 'repeat' })}`;
}

// TODO: Add tests
function pollAtInterval(fetcher, interval, maxPolls) {
	return new Promise((resolve, reject) => {
		let pollingTimeoutId,
			pollCounter = 0;

		const poll = () =>
			fetcher()
				.then(response => {
					if (response.status === 'Failed') {
						clearTimeout(pollingTimeoutId);
						reject('Server returned an error while trying to save');
						return;
					}

					if (pollCounter >= maxPolls - 1) {
						clearTimeout(pollingTimeoutId);
						reject(`Server did not reply after ${maxPolls} polls`);
						return;
					}

					if (
						response.status === 'Succeeded' ||
						response.percentComplete === 100
					) {
						clearTimeout(pollingTimeoutId);
						resolve();
					} else {
						pollCounter++;
						pollingTimeoutId = setTimeout(poll, interval);
					}
				})
				.catch(err => {
					reject(err);
				});

		poll();
	});
}

function fetchResource(resource, options, headers) {
	return fetch(resource, {
		...options,
		headers,
	}).then(response => response.json());
}

export function pollResource(
	resource,
	options = {},
	pollingInterval = 1000,
	maxPolls = 6
) {
	return new Promise((resolve, reject) => {
		acquireToken().then(token => {
			const headers = getRequestHeaders(token);
			pollAtInterval(
				() => fetchResource(resource, options, headers),
				pollingInterval,
				maxPolls
			)
				.then(resolve)
				.catch(reject);
		});
	});
}

export function getMutate(resource, options = {}) {
	const mergedOptions = {
		mock: false,
		method: 'POST',
		...options,
	};
	let url = getApiUrl(resource, mergedOptions.mock);
	url = updateUrlToDemoMode(url);

	const mutate = (body = {}) => {
		return new Promise((resolve, reject) => {
			acquireToken().then(token => {
				const headers = getRequestHeaders(token);
				const mergedHeaders = {
					...headers,
					...mergedOptions.headers,
				};
				fetch(url, {
					...mergedOptions,
					headers: mergedHeaders,
					body: JSON.stringify(body),
				})
					.then(response => {
						if (!response.ok) {
							return response.text().then(textResponse => {
								try {
									const jsonResponse = JSON.parse(textResponse);
									return Promise.reject({ ...jsonResponse, response });
								} catch (_e) {
									return Promise.reject(textResponse);
								}
							});
						}

						return resolve(response);
					})
					.catch(e => {
						if (isDemoMode) {
							handleApiError(url, true);
						}

						if (e.response) {
							if (e.response.status === 500) {
								handleApiError(e.correlationId);
							}

							if (e.response.status === 403) {
								navigateToAccessDenied(e.aadUrl, e.aadName);
							}
						}

						return reject(e);
					});
			});
		});
	};

	return mutate;
}

export function serializeQueryParams(params = []) {
	const searchParams = new URLSearchParams();

	Object.keys(params).forEach(key => {
		const value = params[key];
		// Filter null, undefined, empty string
		if (value != null && value !== '') {
			if (Array.isArray(value)) {
				value.forEach(valuePart => searchParams.append(key, valuePart));
			} else if (typeof value !== 'object') {
				searchParams.append(key, value);
			}
		}
	});

	return searchParams.toString();
}

const MAX_PARAMS_LENGTH = 1900;

export const serializeExceedParams = async (
	params,
	propName,
	maxPropLength
) => {
	const { [propName]: prop, ...plainParams } = params;
	const plainSerialized = serializeQueryParams(plainParams);
	const arrayValue =
		params[propName] && maxPropLength
			? params[propName].slice(0, maxPropLength)
			: params[propName];

	if (!arrayValue || !arrayValue.length) {
		return plainSerialized;
	}

	const arrayQuery = serializeQueryParams({ [propName]: arrayValue });

	// storedQueryId already exists
	if (params.storedQueryId) {
		const storedQuery = await fetchStoredQuery(params.storedQueryId);
		const requestParams = [
			...storedQuery,
			...arrayValue.map(item => ({
				key: propName,
				value: item,
			})),
		];

		const { id } = await createStoredQuery(requestParams);
		const { storedQueryId, [propName]: prop, ...restParams } = plainParams;

		return `${serializeQueryParams(restParams)}&storedQueryId=${id}`;
	}

	// params exceed max length
	if (`${plainSerialized}&${arrayQuery}`.length > MAX_PARAMS_LENGTH) {
		const { id } = await createStoredQuery(
			arrayValue.map(item => ({
				key: propName,
				value: item,
			}))
		);
		const { [propName]: prop, ...restParams } = plainParams;

		return `${serializeQueryParams(restParams)}&storedQueryId=${id}`;
	}

	return `${plainSerialized}&${arrayQuery}`;
};
